/**
 * The sound.
 *
 * <b>Synthesised, not a file.</b> Two notes from an oscillator is a dozen lines and
 * no asset: no 20 KB mp3 in the bundle, no second request that can 404 behind a
 * misconfigured static route, nothing to go missing when somebody changes the base
 * path, and no licence question about where the sample came from. It also sounds the
 * same on every platform, which a system sound does not.
 *
 * <b>A browser will not let a page make noise until its user has interacted with
 * it.</b> An AudioContext created on page load starts `suspended`, and calling
 * `start()` on a node inside it does nothing — silently, with no error to catch. So
 * the context is created lazily on the first attempt to play and a one-time listener
 * resumes it on the first real click anywhere in the app. An agent who has loaded
 * the tab and not touched it yet gets no sound; the moment they click anything, they
 * do. Trying to force it earlier does not work and only produces console warnings.
 */

let context: AudioContext | null = null;
let unlocking = false;

/** The context, or null where the browser has no Web Audio at all. */
function ensureContext(): AudioContext | null {
  if (context) return context;

  try {
    const Ctor = window.AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    context = new Ctor();
  } catch {
    // A browser with audio disabled by policy. Nothing to do and nothing to report:
    // a missing chime must never be an error the agent has to read.
    return null;
  }

  armUnlock();
  return context;
}

/**
 * Resume the context on the first gesture.
 *
 * `once` and on the capture phase, so it runs whatever the app's own handlers do
 * with the event, and removes itself afterwards. `pointerdown` and `keydown` both,
 * because somebody who tabs into the app and presses Enter has interacted just as
 * much as somebody who clicked.
 */
function armUnlock(): void {
  if (unlocking || !context) return;
  unlocking = true;

  const resume = () => { void context?.resume().catch(() => { /* nothing to say */ }); };

  window.addEventListener('pointerdown', resume, { once: true, capture: true });
  window.addEventListener('keydown', resume, { once: true, capture: true });
}

/**
 * A short two-note rise. Quiet on purpose — this plays in an office, and a
 * notification sound that makes people jump is a notification sound people disable.
 *
 * The gain envelope is not decoration: an oscillator switched on and off at full
 * amplitude produces an audible click at each end, which is most of what makes a
 * synthesised beep sound cheap. Ramping in over 8ms and out exponentially is the
 * difference between a chime and a tick.
 */
export function playChime(): void {
  const ctx = ensureContext();
  if (!ctx) return;

  // Still suspended: the agent has not interacted with the page yet. The unlock
  // listener is armed, so the next one will play. Returning quietly is the whole
  // handling — there is no way to ask for permission and nothing to tell anybody.
  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => { /* nothing to say */ });
    if (ctx.state === 'suspended') return;
  }

  const now = ctx.currentTime;

  // A major third, A5 to C#6. Two notes rather than one: a single tone reads as a
  // system error sound, and a rising interval reads as "something arrived".
  for (const [frequency, at] of [[880, 0], [1108.73, 0.09]] as const) {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    // Triangle, not sine or square. A sine is too soft to hear across a desk over
    // a fan; a square is harsh at these frequencies.
    oscillator.type = 'triangle';
    oscillator.frequency.value = frequency;

    gain.gain.setValueAtTime(0.0001, now + at);
    gain.gain.exponentialRampToValueAtTime(0.14, now + at + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.16);

    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(now + at);
    oscillator.stop(now + at + 0.18);
  }
}
